import React from 'react';

import { bemBlock } from '../../modules/bem';

import './IconCheckbox.less';

const block = bemBlock('icon-checkbox');

type IconCheckboxPropsBase = {
  id?: string;
  name?: string;
  checked: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  checkedIcon: React.ReactElement;
  uncheckedIcon: React.ReactElement;
  'data-role'?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

type IconCheckboxPropsIndeterminate = IconCheckboxPropsBase & {
  indeterminate: boolean;
  indeterminateIcon: React.ReactElement;
};

type IconCheckboxProps = IconCheckboxPropsBase & {
  indeterminate?: boolean;
  indeterminateIcon?: React.ReactElement;
};

export function IconCheckbox(props: IconCheckboxPropsBase): JSX.Element;
export function IconCheckbox(props: IconCheckboxPropsIndeterminate): JSX.Element;

export function IconCheckbox({
  'aria-label': ariaLabel,
  'data-role': dataRole,
  indeterminateIcon,
  uncheckedIcon,
  checkedIcon,
  checked = false,
  indeterminate,
  className,
  disabled,
  onChange,
  name,
  id,
}: IconCheckboxProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useLayoutEffect(() => {
    if (!inputRef.current) {
      return;
    }

    if (indeterminate) {
      inputRef.current.indeterminate = true;
    } else {
      inputRef.current.indeterminate = false;
    }
  }, [indeterminate]);

  const icon = React.useMemo(() => {
    if (indeterminate) {
      return indeterminateIcon;
    }

    return checked ? checkedIcon : uncheckedIcon;
  }, [checked, checkedIcon, indeterminate, indeterminateIcon, uncheckedIcon]);

  return (
    <label aria-label={ariaLabel} className={block({ extra: className })}>
      <input
        id={id}
        name={name}
        ref={inputRef}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        onChange={onChange}
        className={block('input')}
        data-role={dataRole}
      />
      {icon &&
        React.cloneElement(icon, {
          'aria-hidden': true,
        })}
    </label>
  );
}
